/* ==========================================================================
   Sammy's Catering & Co — theme behaviour
   Vanilla custom elements, no dependencies. Every component re-initialises on
   Shopify's theme-editor events so the customiser preview stays live.
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';

  /* ------------------------------------------------------------------------
     Drawer (mobile navigation + anything else that slides in)
     ------------------------------------------------------------------------ */
  var focusableSelector =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var nodes = Array.prototype.filter.call(
      container.querySelectorAll(focusableSelector),
      function (el) { return el.offsetParent !== null; }
    );
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.close = this.close.bind(this);

      var overlay = this.querySelector('.drawer__overlay');
      if (overlay) overlay.addEventListener('click', this.close);

      this.querySelectorAll('[data-drawer-close]').forEach(
        function (btn) { btn.addEventListener('click', this.close); }.bind(this)
      );
    }

    open(opener) {
      this.opener = opener || null;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);

      var target = this.querySelector('[data-drawer-focus]') || this.querySelector(focusableSelector);
      if (target) window.requestAnimationFrame(function () { target.focus(); });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener) {
        this.opener.setAttribute('aria-expanded', 'false');
        this.opener.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (event.key === 'Tab') trapFocus(this, event);
    }
  }

  if (!customElements.get('site-drawer')) customElements.define('site-drawer', DrawerElement);

  function bindDrawerOpeners(scope) {
    (scope || document).querySelectorAll('[data-drawer-open]').forEach(function (btn) {
      if (btn.dataset.drawerBound === 'true') return;
      btn.dataset.drawerBound = 'true';
      btn.addEventListener('click', function () {
        var drawer = document.getElementById(btn.getAttribute('data-drawer-open'));
        if (!drawer) return;
        btn.setAttribute('aria-expanded', 'true');
        drawer.open(btn);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Marquee — duplicates its own content until the track is wide enough to
     loop seamlessly at any viewport width.
     ------------------------------------------------------------------------ */
  class MarqueeElement extends HTMLElement {
    connectedCallback() {
      /* Marked with data-marquee-track so any strip can use this, whatever
         its own class naming is. */
      this.track = this.querySelector('[data-marquee-track]') || this.querySelector('.marquee__track');
      if (!this.track) return;
      this.source = this.track.innerHTML;
      this.build();
      this.resizeObserver = new ResizeObserver(this.build.bind(this));
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    build() {
      if (this.building) return;
      this.building = true;

      this.track.innerHTML = this.source;
      var unitWidth = this.track.scrollWidth;
      if (unitWidth > 0) {
        // Repeat until one "half" of the track covers the viewport, then
        // mirror it so translateX(-50%) lands on an identical frame.
        var repeats = Math.max(2, Math.ceil(this.offsetWidth / unitWidth) + 1);
        var html = '';
        for (var i = 0; i < repeats; i++) html += this.source;
        this.track.innerHTML = html + html;
      }

      this.building = false;
    }
  }

  if (!customElements.get('marquee-ticker')) customElements.define('marquee-ticker', MarqueeElement);

  /* ------------------------------------------------------------------------
     Scroll rail — arrow controls for a horizontally scrolling track.

     The track scrolls on its own with a trackpad, a touch swipe or the
     keyboard; this only adds the arrows a mouse user has no gesture for, and
     greys them out at each end so the rail's extent is legible.
     ------------------------------------------------------------------------ */
  class ScrollRailElement extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.nav = this.querySelector('[data-rail-nav]');

      this.onScroll = this.sync.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prev) this.prev.addEventListener('click', this.step.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.step.bind(this, 1));

      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
      this.sync();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    /* One card plus its gap, so a click always lands on a snap point. */
    stride() {
      var card = this.track.firstElementChild;
      if (!card) return this.track.clientWidth;
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    step(direction) {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: this.stride() * direction,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    sync() {
      // Sub-pixel layout means the end is never exactly scrollWidth.
      var slack = 2;
      var max = this.track.scrollWidth - this.track.clientWidth;
      var atStart = this.track.scrollLeft <= slack;
      var atEnd = this.track.scrollLeft >= max - slack;

      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      // Nothing overflows: hide the arrows, never the track itself.
      if (this.nav) this.nav.hidden = max <= slack;
    }
  }

  if (!customElements.get('scroll-rail')) customElements.define('scroll-rail', ScrollRailElement);

  /* ------------------------------------------------------------------------
     Announcement rotator (mobile only)
     ------------------------------------------------------------------------ */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement__item'));
      if (this.items.length < 2) {
        if (this.items[0]) this.items[0].classList.add('is-active');
        return;
      }
      this.index = 0;
      this.items[0].classList.add('is-active');
      var speed = parseInt(this.dataset.speed || '4000', 10);
      this.timer = window.setInterval(this.advance.bind(this), speed);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }

    advance() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }

  if (!customElements.get('announcement-rotator')) customElements.define('announcement-rotator', AnnouncementRotator);

  /* ------------------------------------------------------------------------
     Disclosure — accordions, footer columns, product tabs.
     data-disclosure-mobile-only keeps the panel open above 750px.
     ------------------------------------------------------------------------ */
  function initDisclosures(scope) {
    (scope || document).querySelectorAll('[data-disclosure]').forEach(function (toggle) {
      if (toggle.dataset.disclosureBound === 'true') return;
      toggle.dataset.disclosureBound = 'true';

      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;

      var mobileOnly = toggle.hasAttribute('data-disclosure-mobile-only');
      var mql = window.matchMedia(MOBILE_QUERY);

      var sync = function () {
        if (mobileOnly && !mql.matches) {
          panel.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
        } else {
          var open = toggle.dataset.openState === 'true';
          panel.hidden = !open;
          toggle.setAttribute('aria-expanded', String(open));
        }
      };

      toggle.dataset.openState = String(toggle.getAttribute('aria-expanded') === 'true');
      sync();

      toggle.addEventListener('click', function () {
        if (mobileOnly && !mql.matches) return;
        toggle.dataset.openState = toggle.dataset.openState === 'true' ? 'false' : 'true';
        sync();
      });

      if (mql.addEventListener) mql.addEventListener('change', sync);
      else if (mql.addListener) mql.addListener(sync);
    });
  }

  /* ------------------------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------------------------ */
  function initQuantity(scope) {
    (scope || document).querySelectorAll('.quantity').forEach(function (wrap) {
      if (wrap.dataset.qtyBound === 'true') return;
      wrap.dataset.qtyBound = 'true';

      var input = wrap.querySelector('input');
      if (!input) return;

      wrap.querySelectorAll('button[data-qty]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var step = btn.getAttribute('data-qty') === 'up' ? 1 : -1;
          var min = parseInt(input.getAttribute('min') || '0', 10);
          var next = (parseInt(input.value, 10) || 0) + step;
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Product gallery — thumbnails swap the main image
     ------------------------------------------------------------------------ */
  function initGallery(scope) {
    (scope || document).querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryBound === 'true') return;
      gallery.dataset.galleryBound = 'true';

      var main = gallery.querySelector('[data-gallery-main]');
      if (!main) return;

      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-src');
          var srcset = thumb.getAttribute('data-srcset');
          var alt = thumb.getAttribute('data-alt') || '';
          if (src) main.setAttribute('src', src);
          if (srcset) main.setAttribute('srcset', srcset);
          main.setAttribute('alt', alt);

          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (other) {
            other.setAttribute('aria-current', String(other === thumb));
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Variant picker — keeps price, availability and the hidden id in sync
     ------------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.addEventListener('change', this.onChange.bind(this));

      var data = this.querySelector('[type="application/json"]');
      this.variants = data ? JSON.parse(data.textContent) : [];
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map(function (input) {
        return input.value;
      });
    }

    onChange() {
      var chosen = this.selectedOptions();
      var match = this.variants.find(function (variant) {
        return variant.options.every(function (option, index) { return option === chosen[index]; });
      });

      this.updateUrl(match);
      this.updateFormId(match);
      this.updatePrice(match);
      this.updateButton(match);
    }

    updateUrl(variant) {
      if (!variant || !this.dataset.url) return;
      window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
    }

    updateFormId(variant) {
      var input = this.form && this.form.querySelector('input[name="id"]');
      if (input) input.value = variant ? variant.id : '';
    }

    updatePrice(variant) {
      var target = document.getElementById('price-' + this.dataset.section);
      if (!target) return;
      if (!variant) { target.innerHTML = ''; return; }
      target.innerHTML = variant.compare_at_price > variant.price
        ? '<del>' + this.money(variant.compare_at_price) + '</del><ins>' + this.money(variant.price) + '</ins>'
        : this.money(variant.price);
    }

    money(cents) {
      var format = this.dataset.moneyFormat || '${{amount}}';
      var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }

    updateButton(variant) {
      if (!this.form) return;
      var button = this.form.querySelector('[data-add-to-cart]');
      if (!button) return;

      var available = variant && variant.available;
      button.disabled = !available;
      button.textContent = variant
        ? (available ? button.dataset.labelAvailable : button.dataset.labelSoldOut)
        : button.dataset.labelUnavailable;
    }
  }

  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------------
     Sticky header offset — exposes --header-height for scroll-margin
     ------------------------------------------------------------------------ */
  function measureHeader() {
    var header = document.querySelector('.header');
    if (!header) return;
    document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function init(scope) {
    bindDrawerOpeners(scope);
    initDisclosures(scope);
    initQuantity(scope);
    initGallery(scope);
    measureHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); watchCartCount(); watchCartPopup(); initHeaderMenus(document); });
  } else {
    init(document);
    watchCartCount();
    watchCartPopup();
    initHeaderMenus(document);
  }

  /* ------------------------------------------------------------------------
     Cart count

     The existing theme's add-to-cart script re-renders Shopify's
     cart-icon-bubble section and writes it into #cart-icon-bubble. That element
     is hidden in our header (it carries the other theme's markup), so watch it
     and copy the number onto the badge the design actually shows.
     ------------------------------------------------------------------------ */
  function readBubbleCount(bubble) {
    var span = bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (span) return span.textContent.trim();
    return bubble.querySelector('.cart-count-bubble') ? '' : '0';
  }

  function syncCartCount() {
    var bubble = document.getElementById('cart-icon-bubble');
    var badge = document.querySelector('[data-cart-link] .header__cart-count');
    if (!bubble || !badge) return;

    var count = readBubbleCount(bubble);
    badge.textContent = count;
    badge.hidden = !count || count === '0';
  }

  function watchCartCount() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || bubble.dataset.sammysWatched) return;
    bubble.dataset.sammysWatched = 'true';
    new MutationObserver(syncCartCount).observe(bubble, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Cart pop-up position

     The pop-up is pinned to the screen (sammys-chrome.css). As it opens, put
     it just under the header if the header is in view, or near the top of the
     screen once the header has scrolled away.
     ------------------------------------------------------------------------ */
  function watchCartPopup() {
    var pop = document.getElementById('cart-notification');
    if (!pop || pop.dataset.sammysWatched) return;
    pop.dataset.sammysWatched = 'true';

    new MutationObserver(function () {
      if (!pop.classList.contains('active')) return;
      var header = document.querySelector('.header-section.sammys');
      var bottom = header ? header.getBoundingClientRect().bottom : 0;
      pop.style.setProperty('--cart-pop-top', Math.max(12, Math.round(bottom) + 12) + 'px');
    }).observe(pop, { attributes: true, attributeFilter: ['class'] });
  }

  /* ------------------------------------------------------------------------
     Header submenus

     <details> already handles click and the keyboard. This adds what it does
     not: open on hover for pointers, only one panel at a time, and close on
     Escape or a click elsewhere.
     ------------------------------------------------------------------------ */
  function initHeaderMenus(scope) {
    var menus = (scope || document).querySelectorAll('[data-header-menu]');
    if (!menus.length) return;

    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    menus.forEach(function (menu) {
      if (menu.dataset.menuReady === 'true') return;
      menu.dataset.menuReady = 'true';

      var closeTimer = null;

      function closeOthers() {
        document.querySelectorAll('[data-header-menu][open]').forEach(function (other) {
          if (other !== menu) other.open = false;
        });
      }

      menu.addEventListener('toggle', function () {
        if (menu.open) closeOthers();
      });

      if (canHover) {
        menu.addEventListener('mouseenter', function () {
          window.clearTimeout(closeTimer);
          closeOthers();
          menu.open = true;
        });
        menu.addEventListener('mouseleave', function () {
          closeTimer = window.setTimeout(function () { menu.open = false; }, 140);
        });
      }

      /* Leaving the panel by keyboard should close it behind you. */
      menu.addEventListener('focusout', function (event) {
        if (!menu.contains(event.relatedTarget)) menu.open = false;
      });
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.querySelector('[data-header-menu][open]');
    if (!open) return;
    open.open = false;
    var summary = open.querySelector('summary');
    if (summary) summary.focus();
  });

  document.addEventListener('click', function (event) {
    document.querySelectorAll('[data-header-menu][open]').forEach(function (menu) {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });

  window.addEventListener('resize', measureHeader);

  // Theme editor: re-initialise whatever section was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); watchCartCount(); initHeaderMenus(event.target); });
  document.addEventListener('shopify:section:select', function (event) { init(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest('[data-scroll-into-view]');
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  });
})();
